/**
 * Design IDs section of the Code tables tab (Phase 86 slice B, 86-19; review
 * fixes A to D).
 *
 * Where each new product's design ID comes from: "Make design IDs for me"
 * (the default, today's behaviour) or "Use the design IDs on my products"
 * (BYO: product sync reads the front / back design ID and light, dark or
 * medium from the org's own mapped Shopify fields).
 *
 * Who sees what (RBAC four layers, with `updateProductSyncSettings`):
 *   - The control: a platform admin (`isPlatformAdmin`) who is an admin of an
 *     org with store SKUs on. It stays for a platform admin while the saved
 *     source is BYO, so BYO can still be switched off. The server refuses a
 *     change from anyone else (`BYO_SWITCH_NOT_ALLOWED`).
 *   - Everyone else, for a store SKU org (or one already on BYO): one
 *     read-only line with the current state. Any other org: nothing.
 *   - Onboarding passes `showDesignIdSource={false}` to the tab: nothing.
 *
 * The switch saves on its own (the Store SKUs switch pattern): choosing the
 * other option opens a confirm for that direction, and the confirm saves
 * ONLY `designIdSource`, over the loaded server doc (`savePatch`), never the
 * page's dirty buffer. A refusal shows inline and as a toast.
 *
 * The mapped fields are shown read only (`namespace.key` or "Not set") from
 * `shopifyMetafieldMappings.list`, with a link to Product data lookup, where
 * they are changed. A failed read is an error with Retry, never "Not set".
 *
 * Switch-back lock: while the saved source is BYO, one cheap read
 * (`getDesignIdSourceLock`) says whether production already has products
 * synced with the org's own design IDs; then "Make design IDs for me" is
 * disabled with a note.
 */
import { useEffect, useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight, Info, Loader2, Lock } from 'lucide-react';
import { declaredSkuModeIsOn, type ByoDesignPurpose, type DesignIdSource } from '@threadheads/shared';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import { useRole } from '@/hooks/use-role';
import { Skeleton } from '@/components/ui/skeleton';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { NoteLine } from '@/components/shared/NoteLine';
import { SectionError } from '@/components/shared/SectionError';
import { WarningNotice } from '@/components/shared/WarningNotice';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { REFUSAL_KEY, type ProductSyncSettingsBuffer } from '../hooks/useProductSyncSettingsBuffer';

/** The five mapped fields, in reading order. */
const MAPPED_FIELDS = [
  'design_id',
  'back_design_id',
  'design_variant',
  'front_design_variant',
  'back_design_variant',
] as const satisfies readonly ByoDesignPurpose[];

/** Literal keys, so the i18n check sees every one. */
const FIELD_LABEL_KEY: Record<ByoDesignPurpose, string> = {
  design_id: 'designIds.mapping.design_id',
  back_design_id: 'designIds.mapping.back_design_id',
  design_variant: 'designIds.mapping.design_variant',
  front_design_variant: 'designIds.mapping.front_design_variant',
  back_design_variant: 'designIds.mapping.back_design_variant',
};

const OPTION_KEYS: Record<DesignIdSource, { label: string; hint: string }> = {
  generate: { label: 'designIds.option.generate', hint: 'designIds.option.generateHint' },
  metafield: { label: 'designIds.option.metafield', hint: 'designIds.option.metafieldHint' },
};

const SOURCES: readonly DesignIdSource[] = ['generate', 'metafield'];

/** Settings, Integrations: where Product data lookup (the mappings) lives. */
const PRODUCT_DATA_LOOKUP_PATH = '/settings?tab=integrations';

const LINK_CLASS =
  'inline-flex items-center gap-1 font-medium text-threadheads-purple underline underline-offset-2 hover:text-threadheads-purple/80 motion-reduce:transition-none';

export interface DesignIdSourceSectionProps {
  buffer: ProductSyncSettingsBuffer;
}

export function DesignIdSourceSection({ buffer }: DesignIdSourceSectionProps) {
  const { t } = useTranslation('productSync');
  const { isAdmin, isPlatformAdmin } = useRole();
  const saved: DesignIdSource = buffer.serverSettings.designIdSource ?? 'generate';

  const modeQuery = trpc.productSync.getDeclaredSkuMode.useQuery(undefined, { staleTime: 30_000 });
  const storeSkusOn = modeQuery.data ? declaredSkuModeIsOn(modeQuery.data.mode) : false;
  const relevant = storeSkusOn || saved === 'metafield';
  const canControl = isPlatformAdmin() && isAdmin() && relevant;

  if (!relevant) return null;
  if (!canControl) {
    return (
      <div data-testid="design-id-source-readonly">
        <NoteLine as="p" icon={Info} className="px-1">
          {saved === 'metafield' ? t('designIds.readOnly.metafield') : t('designIds.readOnly.generate')}
        </NoteLine>
      </div>
    );
  }
  return <DesignIdSourceControl buffer={buffer} saved={saved} />;
}

function DesignIdSourceControl({ buffer, saved }: { buffer: ProductSyncSettingsBuffer; saved: DesignIdSource }) {
  const { t } = useTranslation('productSync');
  const headingId = useId();
  const helperId = useId();
  const [confirming, setConfirming] = useState<DesignIdSource | null>(null);

  const mappingsQuery = trpc.shopifyMetafieldMappings.list.useQuery(undefined, { staleTime: 60_000 });
  // Only while BYO is saved: the one cheap read that decides the switch-back lock.
  const lockQuery = trpc.productSync.getDesignIdSourceLock.useQuery(undefined, {
    enabled: saved === 'metafield',
    staleTime: 30_000,
    retry: false,
  });
  const locked = saved === 'metafield' && lockQuery.data?.locked === true;

  const mapped = new Map<string, string>();
  for (const row of mappingsQuery.data ?? []) mapped.set(row.purpose, `${row.namespace}.${row.key}`);
  const requiredMissing = mappingsQuery.isSuccess && (!mapped.has('design_id') || !mapped.has('design_variant'));

  const busy = buffer.isSaving;
  const refusal = buffer.designIdSourceRefusal;
  // A refusal closes the confirm; the reason shows inline under the options.
  useEffect(() => {
    if (refusal) setConfirming(null);
  }, [refusal]);

  const choose = (next: string): void => {
    if (busy || next === saved || (next !== 'generate' && next !== 'metafield')) return;
    if (next === 'generate' && locked) return;
    buffer.clearDesignIdSourceRefusal();
    setConfirming(next);
  };

  const closeDialog = (open: boolean): void => {
    if (!open && !busy) setConfirming(null);
  };

  const confirm = (next: DesignIdSource): void => {
    if (busy) return;
    buffer.savePatch({ designIdSource: next }, { successMessage: t('designIds.saved'), onSuccess: () => setConfirming(null) });
  };

  return (
    <section className="rounded-xl border bg-background p-5" aria-labelledby={headingId} data-testid="design-id-source">
      <h3 id={headingId} className="text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">
        {t('designIds.label')}
      </h3>
      <p id={helperId} className="mt-1 max-w-prose text-xs text-muted-foreground">
        {t('designIds.helper')}
      </p>

      <RadioGroup
        value={saved}
        onValueChange={choose}
        disabled={busy}
        aria-labelledby={headingId}
        aria-describedby={helperId}
        className="mt-3 grid gap-2 sm:grid-cols-2"
      >
        {SOURCES.map((option) => (
          <SourceOption key={option} option={option} disabled={busy || (option === 'generate' && locked)} />
        ))}
      </RadioGroup>

      {locked && (
        <NoteLine as="p" icon={Lock} className="mt-3 max-w-prose">
          {t('designIds.lockedNote')}
        </NoteLine>
      )}
      {saved === 'metafield' && lockQuery.isError && (
        <SectionError
          variant="inline"
          className="mt-3"
          body={t('designIds.lockError')}
          onRetry={() => void lockQuery.refetch()}
          retryLabel={t('designIds.retry')}
          retrying={lockQuery.isFetching}
        />
      )}
      {refusal && (
        <p className="mt-3 max-w-prose text-xs text-destructive" role="alert">
          {t(REFUSAL_KEY[refusal])}
        </p>
      )}

      <div className="mt-4 space-y-3 border-t pt-4">
        <p className="text-xs font-medium">{t('designIds.mappingsHeading')}</p>
        {mappingsQuery.isError ? (
          <SectionError
            variant="compact"
            body={t('designIds.listError')}
            onRetry={() => void mappingsQuery.refetch()}
            retryLabel={t('designIds.retry')}
            retrying={mappingsQuery.isFetching}
          />
        ) : mappingsQuery.isLoading ? (
          <div className="space-y-2" aria-busy="true">
            {MAPPED_FIELDS.map((purpose) => (
              <Skeleton key={purpose} className="h-4 w-64 max-w-full" />
            ))}
          </div>
        ) : (
          <>
            {saved === 'metafield' && requiredMissing && (
              <WarningNotice role="status" data-testid="design-id-mappings-missing">
                {t('designIds.mappingsMissing')}
              </WarningNotice>
            )}
            <dl className="grid gap-x-6 gap-y-1.5 text-xs sm:grid-cols-[max-content_minmax(0,1fr)]">
              {MAPPED_FIELDS.map((purpose) => (
                <MappedFieldRow key={purpose} label={t(FIELD_LABEL_KEY[purpose])} value={mapped.get(purpose) ?? null}>
                  {purpose === 'back_design_id' ? (
                    <NoteLine className="mt-0.5">{t('designIds.backNote')}</NoteLine>
                  ) : null}
                </MappedFieldRow>
              ))}
            </dl>
          </>
        )}
        <p className="text-xs">
          <Link to={PRODUCT_DATA_LOOKUP_PATH} className={LINK_CLASS}>
            {t('designIds.changeMappings')}
            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </Link>
        </p>
      </div>

      {/* Turn on: purple confirm. Waits for the mappings and refuses to start without them. */}
      <AlertDialog open={confirming === 'metafield'} onOpenChange={closeDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('designIds.turnOn.title')}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>{t('designIds.turnOn.body')}</p>
                {requiredMissing && <WarningNotice>{t('designIds.mappingsMissing')}</WarningNotice>}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>{t('designIds.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90 motion-reduce:transition-none"
              disabled={busy || !mappingsQuery.isSuccess || requiredMissing}
              onClick={(event) => {
                event.preventDefault();
                confirm('metafield');
              }}
            >
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
              {busy ? t('designIds.saving') : t('designIds.turnOn.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Turn off: reversible outside production, so the confirm is orange outline (DESIGN section 13.6). */}
      <AlertDialog open={confirming === 'generate'} onOpenChange={closeDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('designIds.turnOff.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('designIds.turnOff.body')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>{t('designIds.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="gap-1.5 border border-orange-300 bg-background text-orange-700 hover:bg-orange-50 hover:text-orange-700 motion-reduce:transition-none"
              disabled={busy}
              onClick={(event) => {
                event.preventDefault();
                confirm('generate');
              }}
            >
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
              {busy ? t('designIds.saving') : t('designIds.turnOff.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

/** One radio option as a clickable card (the Store SKUs switch's `ModeOption`). */
function SourceOption({ option, disabled }: { option: DesignIdSource; disabled: boolean }) {
  const { t } = useTranslation('productSync');
  const id = useId();
  const labelId = `${id}-label`;
  const bodyId = `${id}-body`;
  return (
    <label
      htmlFor={id}
      className={cn(
        'flex min-w-0 items-start gap-3 rounded-lg border bg-muted/30 p-3 transition-colors duration-200 ease-out motion-reduce:transition-none',
        'has-[[data-state=checked]]:border-threadheads-purple/60 has-[[data-state=checked]]:bg-threadheads-purple/[0.04]',
        disabled ? 'cursor-default opacity-70' : 'cursor-pointer hover:bg-muted/50',
      )}
    >
      <RadioGroupItem
        value={option}
        id={id}
        disabled={disabled}
        aria-labelledby={labelId}
        aria-describedby={bodyId}
        className="mt-0.5"
      />
      <span className="min-w-0 space-y-1">
        <span id={labelId} className="block text-sm font-medium">
          {t(OPTION_KEYS[option].label)}
        </span>
        <span id={bodyId} className="block text-xs leading-4 text-muted-foreground">
          {t(OPTION_KEYS[option].hint)}
        </span>
      </span>
    </label>
  );
}

/** One read-only mapping line: the field's name, then `namespace.key` or "Not set". */
function MappedFieldRow({ label, value, children }: { label: string; value: string | null; children?: ReactNode }) {
  const { t } = useTranslation('productSync');
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0">
        {value ? (
          <span className="break-all font-mono">{value}</span>
        ) : (
          <span className="text-muted-foreground">{t('designIds.notSet')}</span>
        )}
        {children}
      </dd>
    </>
  );
}
